/* Mobile fix: drawer = full screen height, header + checkout footer always visible,
   only the item list scrolls. Overrides Dawn's max-height:650px rule that made
   the whole drawer scroll and pushed Check out off-screen. */
(function injectCartDrawerLayoutFix() {
  if (document.getElementById('cart-drawer-layout-fix')) return;
  const style = document.createElement('style');
  style.id = 'cart-drawer-layout-fix';
  style.textContent = `
    cart-drawer,
    cart-drawer .drawer {
      height: 100vh;
      height: 100dvh;
    }
    cart-drawer .drawer__inner {
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    cart-drawer cart-drawer-items {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }
    cart-drawer .drawer__header,
    cart-drawer .drawer__footer {
      flex-shrink: 0;
    }
    @media screen and (max-height: 650px) {
      cart-drawer cart-drawer-items { overflow-y: auto; }
      cart-drawer .drawer__inner { overflow: hidden; }
    }
    /* View cart + Check out side by side */
    cart-drawer .cart__ctas {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
    }
    cart-drawer .cart__ctas > * {
      width: 100%;
      margin: 0;
    }
    cart-drawer .cart-drawer__view-cart {
      min-width: 0;
      text-align: center;
    }
  `;
  document.head.appendChild(style);
})();

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Keep a "View cart" button next to Check out, also after the drawer re-renders
    this.ensureViewCartButton();
    new MutationObserver(() => this.ensureViewCartButton()).observe(this, { childList: true, subtree: true });
  }

  ensureViewCartButton() {
    const ctas = this.querySelector('.cart__ctas');
    if (!ctas || ctas.querySelector('.cart-drawer__view-cart')) return;

    const lang = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
    const labels = { fi: 'Näytä ostoskori', et: 'Vaata ostukorvi', sv: 'Visa varukorg', en: 'View cart' };
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    const link = document.createElement('a');
    link.href = `${root}cart`;
    link.className = 'button button--secondary cart-drawer__view-cart';
    link.textContent = labels[lang] || labels.en;

    ctas.insertBefore(link, ctas.firstChild);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);